import React from 'react'
import { all_routes } from '../../router/all_routes'
import Breadcrumb from '../../../core/common/Breadcrumb/breadcrumb'
import ProfileCard from '../common/profileCard'
import StudentSidebar from '../common/studentSidebar'
import { Link } from 'react-router-dom'
import SettingsLinks from './settingsLinks/settingsLinks'
import ImageWithBasePath from '../../../core/common/imageWithBasePath'
import CustomSelect from '../../../core/common/commonSelect'
import { Gender } from '../../../core/common/selectOption/json/selectOption'
import { DatePicker } from 'antd'
import SettingsModal from './settingsModal/settingsModal'
import {
  getUserProfile,
  postUserProfile,
  postUserProfilePicture,
} from '../../../api/user'
import { message } from 'antd'

const StudentSettings = () => {
  const route = all_routes

  // State untuk form
  const [form, setForm] = React.useState({
    fullname: '',
    nik: '',
    email: '',
    phone_number: '',
    gender: '',
    dob: '',
    profession: '',
    agency: '',
    bio: '',
  })
  const [loading, setLoading] = React.useState(false)
  const [initLoading, setInitLoading] = React.useState(true)

  // State untuk profile picture upload
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null)
  const [uploadLoading, setUploadLoading] = React.useState(false)
  const [profilePicture, setProfilePicture] = React.useState<string | null>(
    null,
  )
  const [phoneError, setPhoneError] = React.useState<string | null>(null)
  const [nikError, setNikError] = React.useState<string | null>(null)

  React.useEffect(() => {
    getUserProfile()
      .then((data) => {
        setForm({
          fullname: data.user.fullname || '',
          nik: data.user.nik || '',
          email: data.user.email || '',
          phone_number: data.user.phone_number || '',
          gender: data.user.gender || '',
          dob: data.user.dob || '',
          profession: data.user.profession || '',
          agency: data.user.agency || '',
          bio: data.user.bio || '',
        })
        setProfilePicture(data.user.profile_picture || null)
        setInitLoading(false)
      })
      .catch(() => {
        setInitLoading(false)
      })
  }, [])

  // Listen for localStorage changes to keep profilePicture in sync
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user' && e.newValue) {
        try {
          const userData = JSON.parse(e.newValue)
          setProfilePicture(userData.profile_picture || null)
          // Also update form data if needed
          setForm((prev) => ({
            ...prev,
            fullname: userData.fullname || prev.fullname,
            nik: userData.nik || prev.nik,
            email: userData.email || prev.email,
            phone_number: userData.phone_number || prev.phone_number,
            gender: userData.gender || prev.gender,
            dob: userData.dob || prev.dob,
            profession: userData.profession || prev.profession,
            agency: userData.agency || prev.agency,
            bio: userData.bio || prev.bio,
          }))
        } catch (error) {
          console.error('Failed to parse user data from localStorage:', error)
        }
      }
    }

    // Also listen for custom events (for same-tab updates)
    const handleCustomStorageChange = () => {
      try {
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const userData = JSON.parse(userStr)
          setProfilePicture(userData.profile_picture || null)
          // Also update form data if needed
          setForm((prev) => ({
            ...prev,
            fullname: userData.fullname || prev.fullname,
            nik: userData.nik || prev.nik,
            email: userData.email || prev.email,
            phone_number: userData.phone_number || prev.phone_number,
            gender: userData.gender || prev.gender,
            dob: userData.dob || prev.dob,
            profession: userData.profession || prev.profession,
            agency: userData.agency || prev.agency,
            bio: userData.bio || prev.bio,
          }))
        }
      } catch (error) {
        console.error('Failed to parse user data from localStorage:', error)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('localStorageUpdate', handleCustomStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener(
        'localStorageUpdate',
        handleCustomStorageChange,
      )
    }
  }, [])

  // Cleanup preview URL when component unmounts
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const handleChange = (e: any) => {
    const { name, value } = e.target
    let nextValue = value
    if (name === 'phone_number') {
      if (typeof value === 'string' && value.startsWith('0')) {
        nextValue = `62${value.slice(1)}`
      }
    }
    setForm((prev) => ({ ...prev, [name]: nextValue }))
    if (name === 'phone_number') {
      const phone = (nextValue || '').trim()
      if (!phone) {
        setPhoneError(null)
      } else if (!phone.startsWith('62') || phone.length < 10) {
        setPhoneError('Nomor HP harus diawali 62 dan minimal 10 karakter')
      } else {
        setPhoneError(null)
      }
    }
    if (name === 'nik') {
      const nik = (value || '').trim()
      if (!nik) {
        setNikError(null)
      } else if (!/^\d{16}$/.test(nik)) {
        setNikError('NIK harus 16 digit angka')
      } else {
        setNikError(null)
      }
    }
  }

  const handleSelectChange = (value: string) => {
    setForm((prev) => ({ ...prev, gender: value }))
  }

  const handleDateChange = (date: any, dateString: string) => {
    setForm((prev) => ({ ...prev, dob: dateString }))
  }

  // File handling functions
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        message.error('Please select a valid image file (JPG, JPEG, or PNG)')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        message.error('File size must be less than 5MB')
        return
      }

      setSelectedFile(file)
      const url = URL.createObjectURL(file)
      setPreviewUrl(url)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      message.error('Please select a file first')
      return
    }

    setUploadLoading(true)
    try {
      const response = await postUserProfilePicture(selectedFile)
      setProfilePicture(response.user.profile_picture)
      setSelectedFile(null)
      setPreviewUrl(null)
      message.success('Profile picture updated successfully')
    } catch (err) {
      message.error('Failed to upload profile picture')
    } finally {
      setUploadLoading(false)
    }
  }

  const handleDelete = () => {
    setSelectedFile(null)
    setPreviewUrl(null)
    // Note: API doesn't seem to have a delete endpoint, so we just clear the local state
    message.info('File selection cleared')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Normalize phone locally to avoid async state issues
    const normalizedPhone = (form.phone_number || '').startsWith('0')
      ? `62${(form.phone_number || '').slice(1)}`
      : form.phone_number || ''
    const nik = (form.nik || '').trim()
    if (!/^\d{16}$/.test(nik)) {
      setNikError('NIK harus 16 digit angka')
      message.error('NIK harus 16 digit angka')
      return
    }
    const phone = normalizedPhone.trim()
    if (!phone.startsWith('62') || phone.length < 10) {
      setPhoneError('Nomor HP harus diawali 62 dan minimal 10 karakter')
      message.error('Nomor HP harus diawali 62 dan minimal 10 karakter')
      return
    }
    setLoading(true)
    try {
      const payload: Record<string, any> = {}
      const addIfNotEmpty = (key: string, value: any) => {
        if (value === undefined || value === null) return
        if (typeof value === 'string') {
          const v = value.trim()
          if (v !== '') payload[key] = v
        } else {
          payload[key] = value
        }
      }

      // Required or validated fields
      addIfNotEmpty('fullname', form.fullname)
      addIfNotEmpty('gender', form.gender)
      addIfNotEmpty('nik', form.nik)
      addIfNotEmpty('dob', form.dob)
      addIfNotEmpty('profession', form.profession)
      addIfNotEmpty('phone_number', phone)

      // Optional fields (omit if empty)
      addIfNotEmpty('agency', form.agency)
      addIfNotEmpty('bio', form.bio)

      await postUserProfile(payload)
      message.success('Profile updated successfully')
    } catch (err) {
      message.error('Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const getModalContainer = () => {
    const modalElement = document.getElementById('add_assignment')
    return modalElement ? modalElement : document.body // Fallback to document.body if modalElement is null
  }

  return (
    <>
      <Breadcrumb title="Settings" />
      <div className="content">
        <div className="container">
          {/* profile box */}
          <ProfileCard />
          {/* profile box */}
          <div className="row">
            {/* sidebar */}
            <StudentSidebar />
            {/* sidebar */}
            <div className="col-lg-9">
              <div className="mb-3">
                <h5>Settings</h5>
              </div>
              <SettingsLinks />
              <div className="card">
                <div className="card-body">
                  {initLoading ? (
                    <div className="text-center my-5">Loading...</div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <div className="profile-upload-group">
                        <div className="d-flex align-items-center">
                          <Link
                            to={route.studentProfile}
                            className="avatar flex-shrink-0 avatar-xxxl avatar-rounded border me-3"
                            style={{
                              width: '80px',
                              height: '80px',
                              overflow: 'hidden',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <ImageWithBasePath
                              srcApi={profilePicture}
                              src={previewUrl || 'assets/img/user/user-02.jpg'}
                              alt="Profile"
                              className="img-fluid"
                              style={{
                                objectFit: 'cover',
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                              }}
                            />
                          </Link>
                          <div className="profile-upload-head">
                            <h6>
                              <Link to={route.studentProfile}>
                                Profile Photo
                              </Link>
                            </h6>
                            <p className="fs-14 mb-0">
                              PNG or JPG no bigger than 5MB. Recommended size:
                              800x800px
                            </p>
                            <div className="new-employee-field">
                              <div className="d-flex align-items-center mt-2">
                                <div className="image-upload position-relative mb-0 me-2 d-flex align-items-center gap-2">
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                    id="profile-picture-input"
                                  />
                                  <label
                                    htmlFor="profile-picture-input"
                                    className="btn bg-gray-100 btn-sm rounded-pill"
                                    style={{ cursor: 'pointer' }}
                                  >
                                    Select Image
                                  </label>
                                  {selectedFile && (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-primary btn-sm rounded-pill"
                                        onClick={handleUpload}
                                        disabled={uploadLoading}
                                      >
                                        {uploadLoading
                                          ? 'Uploading...'
                                          : 'Upload'}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-secondary btn-sm rounded-pill"
                                        onClick={handleDelete}
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                              {selectedFile && (
                                <div className="mt-2">
                                  <small className="text-muted">
                                    Selected: {selectedFile.name}
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <div className="edit-profile-info mb-3">
                          <h5 className="mb-1">Personal Details</h5>
                          <p>Edit your personal information</p>
                        </div>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Nama Lengkap{' '}
                                <span className="text-danger"> *</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="fullname"
                                value={form.fullname}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                NIK <span className="text-danger"> *</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control${
                                  nikError ? ' is-invalid' : ''
                                }`}
                                name="nik"
                                value={form.nik}
                                onChange={handleChange}
                              />
                              {nikError && (
                                <div className="invalid-feedback d-block">
                                  {nikError}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Email <span className="text-danger"> *</span>
                              </label>
                              <input
                                type="email"
                                className="form-control"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                disabled
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Nomor HP <span className="text-danger"> *</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control${
                                  phoneError ? ' is-invalid' : ''
                                }`}
                                name="phone_number"
                                value={form.phone_number}
                                onChange={handleChange}
                              />
                              {phoneError && (
                                <div className="invalid-feedback d-block">
                                  {phoneError}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Gender <span className="text-danger"> *</span>
                              </label>
                              <CustomSelect
                                options={Gender}
                                className="select d-flex"
                                placeholder="Select"
                                value={form.gender}
                                onChange={handleSelectChange}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Tanggal Lahir{' '}
                                <span className="text-danger"> *</span>
                              </label>
                              <div className="input-icon-end position-relative">
                                <DatePicker
                                  className="form-control datetimepicker"
                                  getPopupContainer={getModalContainer}
                                  placeholder="dd/mm/yyyy"
                                  value={
                                    form.dob
                                      ? window.moment
                                        ? window.moment(form.dob)
                                        : undefined
                                      : undefined
                                  }
                                  onChange={handleDateChange}
                                />
                                <span className="input-icon-addon">
                                  <i className="isax isax-calendar" />
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Profesi <span className="text-danger"> *</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="profession"
                                value={form.profession}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Instansi <span className="text-danger"> *</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="agency"
                                value={form.agency}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Bio</label>
                              <textarea
                                rows={4}
                                className="form-control"
                                name="bio"
                                value={form.bio}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <button
                              className="btn btn-secondary rounded-pill"
                              type="submit"
                              disabled={loading}
                            >
                              {loading ? 'Updating...' : 'Update Profile'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              </div>
              {/* <div className="card mb-0">
                <div className="card-body">
                  <h5 className="fs-18 mb-3">Delete Account</h5>
                  <h6 className="mb-1">
                    Are you sure you want to delete your account?
                  </h6>
                  <p className="mb-3">
                    Refers to the action of permanently removing a user's
                    account and associated data from a system, service and
                    platform.
                  </p>
                  <Link
                    to="#"
                    className="btn btn-secondary"
                    data-bs-toggle="modal"
                    data-bs-target="#delete_account"
                  >
                    Delete Account
                  </Link>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
      <SettingsModal />
    </>
  )
}

export default StudentSettings
